import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import FormError from "./FormError";
import { validateScore, validateOptionalScore } from "../utils/validation";

/**
 * ScoreInput Component
 * Specialized input component for score entry with comprehensive validation
 *
 * @param {string} label - Input label
 * @param {string} name - Input name/id
 * @param {number} value - Current value
 * @param {function} onChange - Change handler
 * @param {function} onBlur - Blur handler
 * @param {boolean} required - Whether field is required
 * @param {string} error - External error message
 * @param {boolean} disabled - Whether field is disabled
 * @param {string} description - Help text
 * @param {number} maxScore - Maximum score (default: 100)
 * @param {boolean} showValidation - Show validation feedback (default: true)
 * @param {string} placeholder - Placeholder text
 * @param {string} className - Additional CSS classes
 */
const ScoreInput = forwardRef(({
  label,
  name,
  value,
  onChange,
  onBlur,
  required = false,
  error: externalError,
  disabled = false,
  description,
  maxScore = 100,
  showValidation = true,
  placeholder,
  className = "",
}, ref) => {
  // Set default placeholder based on maxScore
  const defaultPlaceholder = `Enter score (0-${maxScore})`;
  const inputPlaceholder = placeholder || defaultPlaceholder;
  const [internalError, setInternalError] = useState("");
  const [touched, setTouched] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  // Expose focus method to parent
  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
  }));

  // Use external error if provided, otherwise use internal error
  const displayError = externalError || (touched && internalError);
  const hasError = !!displayError;

  // Validation state
  const isValid = !hasError && value !== "" && value !== null && value !== undefined;
  const isEmpty = value === "" || value === null || value === undefined;

  // Validate on value change
  useEffect(() => {
    if (touched && value !== "" && value !== null && value !== undefined) {
      const validation = required
        ? validateScore(value, maxScore)
        : validateOptionalScore(value, maxScore);

      setInternalError(validation.error || "");
    }
  }, [value, required, touched, maxScore]);

  const handleChange = (e) => {
    const newValue = e.target.value;

    // Allow empty value
    if (newValue === "") {
      onChange("");
      return;
    }

    // Allow decimal numbers with up to 2 decimal places
    if (/^\d*\.?\d{0,2}$/.test(newValue)) {
      onChange(newValue);
    }
  };

  const handleBlur = (e) => {
    setTouched(true);
    setIsFocused(false);

    // Validate on blur
    if (value !== "" && value !== null && value !== undefined) {
      const validation = required
        ? validateScore(value, maxScore)
        : validateOptionalScore(value, maxScore);

      setInternalError(validation.error || "");
    } else if (required) {
      setInternalError("This field is required");
    }

    // Call parent blur handler
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  // Input border classes based on state
  const getBorderClasses = () => {
    if (hasError) {
      return "border-red-300 focus:border-red-500 focus:ring-red-500";
    }
    if (isValid && showValidation) {
      return "border-green-300 focus:border-green-500 focus:ring-green-500";
    }
    return "border-gray-300 focus:border-primary-500 focus:ring-primary-500";
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Label */}
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Description */}
      <p className="text-xs text-gray-600 mb-2">
        {description || `Enter score out of ${maxScore}`}
      </p>

      {/* Input Container */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          id={name}
          name={name}
          value={value === null || value === undefined ? "" : value}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          disabled={disabled}
          placeholder={inputPlaceholder}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${name}-error` : undefined}
          aria-required={required}
          className={`
            block w-full px-3 py-2 pr-10
            text-gray-900 placeholder-gray-400
            border rounded-lg
            focus:outline-none focus:ring-2 focus:ring-offset-0
            disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
            transition-colors
            ${getBorderClasses()}
          `}
        />

        {/* Validation Icon */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {showValidation && !isEmpty && hasError && (
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
          )}
          {showValidation && !isEmpty && isValid && (
            <CheckCircleIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
          )}
        </div>
      </div>

      {/* Error Message */}
      {displayError && (
        <FormError
          error={displayError}
          id={`${name}-error`}
          className="mt-1"
        />
      )}

      {/* Score Range Hint */}
      {!hasError && !isFocused && isEmpty && (
        <p className="mt-1 text-xs text-gray-500">
          Maximum score: {maxScore}
        </p>
      )}
    </div>
  );
});

ScoreInput.displayName = 'ScoreInput';

/**
 * ScoreInputGroup Component
 * Groups related score inputs together
 */
export const ScoreInputGroup = ({
  title,
  description,
  children,
  className = "",
}) => {
  return (
    <div className={`space-y-4 ${className}`}>
      {title && (
        <div className="border-b border-gray-200 pb-2">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {description && (
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          )}
        </div>
      )}
      <div className="space-y-4">{children}</div>
    </div>
  );
};

/**
 * ScoreInputGrid Component
 * Displays score inputs in a responsive grid
 */
export const ScoreInputGrid = ({
  children,
  columns = 2,
  className = "",
}) => {
  const gridClasses = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  };

  return (
    <div className={`grid ${gridClasses[columns]} gap-4 ${className}`}>
      {children}
    </div>
  );
};

export default ScoreInput;